import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { AuthFacadeService } from '@ib-core/services/auth-facade.service';
import { AuthHttpService } from '@ib-core/services/auth-http.service';
import { ModalsService } from '@ib-core/services/modals.service';
import { MatSnackBarService } from '@ib-core/services/snackbar.service';
import { UiTouchscreenService } from '@ib-core/services/ui-touchscreen.service';
import { FORM_ERRORS } from '@ib-enums/form-control-errors.enum';
import { AbstractBaseComponent } from '@ib-shared/abstract/abstract-base';
import { IAuthUser } from '@ib-shared/interfaces/auth-user.intf';
import {
  ISelectedDocument,
  SELECTED_DOC_FORM_FIELDS,
} from '@ib-shared/modules/selected-docs/selected-docs.component';
import { MediaDevicesService } from '@ib-shared/services/media-device.service';
import { DocumentsRequiredValidator } from '@ib-shared/validation/documents-required.validator';
import { TranslocoPipe } from '@ngneat/transloco';
import { INVESTOR_ACCOUNT_TYPE } from 'credebt-shared';
import { debounceTime, takeUntil, tap } from 'rxjs';
import { SIGN_UP_FIELDS } from 'src/app/views/not-auth/sign-up/enums/sign-up-fields.enum';

import { DashboardFacadeService } from '../../services/dashboard-facade.service';

@Component({
  selector: 'ib-dashboard-add-document-modal',
  templateUrl: './dashboard-add-document-modal.component.html',
  styleUrls: ['./dashboard-add-document-modal.component.scss'],
})
export class DashboardAddDocumentModalComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  public loading = false;
  public docs: Array<ISelectedDocument>;
  public docsMainFormGroup: FormGroup;
  public docsIsAddedCtrl = new FormControl(false, [Validators.requiredTrue]);
  public isDevicesInited = false;
  public isDevicesInitInProgress = false;
  public videoDevicesAvailable = false;
  public docsFormGroupsMap: Record<string, FormGroup> = {};
  public readonly SIGN_UP_FIELDS = SIGN_UP_FIELDS;
  public readonly USER_TYPE = INVESTOR_ACCOUNT_TYPE;
  public docTypes$ = this.facadeS.selectRequiredDocuments().pipe(
    debounceTime(100),
    tap(r => {
      if (r?.length === 1) {
        this.form.get(SIGN_UP_FIELDS.DOC_TYPE).setValue(r[0].type);
      }
    }),
  );
  public modalRef: MatDialogRef<TemplateRef<HTMLElement>>;
  public authUser: IAuthUser;
  public form: FormGroup;

  get showDoubleSelection(): boolean {
    return !this.uiTsS.isTouchScreen && this.videoDevicesAvailable;
  }

  @ViewChild('modalTmpl') modalTmpl: TemplateRef<HTMLElement>;

  @Output() closeMe = new EventEmitter<void>();

  constructor(
    private modalsS: ModalsService,
    private facadeS: DashboardFacadeService,
    private authFasadeS: AuthFacadeService,
    private snackbarS: MatSnackBarService,
    public uiTsS: UiTouchscreenService,
    private fb: FormBuilder,
    private authHttpS: AuthHttpService,
    private translateP: TranslocoPipe,
    private mediaDevicesS: MediaDevicesService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.initAuthUser();
    if (this.uiTsS.isTouchScreen) {
      this.isDevicesInited = true;
    } else {
      this.initDesktopDevices();
    }
  }

  ngOnDestroy(): void {
    this.modalRef?.close();
    super.destroySubscriptions();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.openModal();
    });
  }

  private initAuthUser(): void {
    this.authFasadeS
      .selectAuthUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => (this.authUser = r));
  }

  openModal(): void {
    this.modalRef = this.modalsS.openModal(this.modalTmpl, {
      maxWidth: '95vw',
      maxHeight: '95vh',
    });
    this.modalRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.closeMe.emit();
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      [SIGN_UP_FIELDS.DOC_TYPE]: [undefined, [Validators.required]],
    });
    this.docsMainFormGroup = new FormGroup({}, [
      DocumentsRequiredValidator({
        minAmount: 1,
        errorName: FORM_ERRORS.ATTACHED_DOCS_REQUIRED,
      }),
    ]);
  }

  private initDesktopDevices(): void {
    this.isDevicesInitInProgress = true;
    this.mediaDevicesS
      .selectVideoDevicesState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data.isInited) {
          this.isDevicesInited = true;
          this.isDevicesInitInProgress = false;
          this.videoDevicesAvailable = Boolean(
            data.isInited && data.devices.length && !data.error,
          );
        }
      });
  }

  removeDoc(id: string): void {
    delete this.docsFormGroupsMap[id];
    this.docsMainFormGroup.removeControl(id);
    this.docsMainFormGroup.markAsTouched();
    this.docsMainFormGroup.markAsDirty();
    this.docs = this.docs.filter(i => i.id !== id);
  }

  postDoc(): void {
    this.loading = true;
    this.authHttpS
      .postRegistrationDocument(
        {
          docType: this.form.get(SIGN_UP_FIELDS.DOC_TYPE).value,
        },
        this.docs[0].file,
      )
      .subscribe({
        next: user => {
          this.authFasadeS.setAuthUser(user);
          this.snackbarS.show({
            msg: this.translateP.transform(
              'modals.provide-additional-doc.posted-msg',
            ),
            type: 'success',
          });
          this.modalRef.close();
          this.loading = false;
          this.closeMe?.emit();
        },
        error: err => {
          this.loading = false;
        },
      });
  }

  addDocs(list: Array<ISelectedDocument>): void {
    this.docs = [...(this.docs || []), ...list].sort((a, b) => {
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
    list.forEach(doc => {
      const fg = new FormGroup({
        [SELECTED_DOC_FORM_FIELDS.DOC_NAME]: new FormControl(doc.name, [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(400),
        ]),
        [SELECTED_DOC_FORM_FIELDS.DOCUMENT]: new FormControl(doc, [
          Validators.required,
        ]),
      });
      this.docsFormGroupsMap[doc.id] = fg;
      this.docsMainFormGroup.addControl(doc.id, fg);
    });
    this.docsMainFormGroup.markAsTouched();
    this.docsMainFormGroup.markAsDirty();
  }
}
